import { useState, useCallback } from 'react';
import { procesoAPI } from '../services/api';

/**
 * Prorrateo del módulo Proceso (rediseño 2026-05).
 * Matriz servicio-instancia × cliente con el % de cada costo asignado a cada cliente.
 * El estado `prorrateo` se indexa: { 'service|||instance': { clientId: porcentaje } }.
 */
export const claveSI = (service, instance) => `${service}|||${instance}`;

export const useProrrateo = (showNotification) => {
  const [prorrateo, setProrrateo] = useState({});

  const loadProrrateo = useCallback(async () => {
    try {
      const res = await procesoAPI.getProrrateo();
      const map = {};
      for (const r of res.data.prorrateo || []) {
        const k = claveSI(r.service, r.instance);
        if (!map[k]) map[k] = {};
        map[k][r.client_id] = parseFloat(r.porcentaje) || 0;
      }
      setProrrateo(map);
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo cargar el prorrateo');
    }
  }, [showNotification]);

  const setCelda = useCallback(async (service, instance, clientId, pct) => {
    const k = claveSI(service, instance);
    setProrrateo((prev) => {
      const next = { ...prev };
      const row = { ...(next[k] || {}) };
      if (pct > 0) row[clientId] = pct;
      else delete row[clientId];
      next[k] = row;
      return next;
    });
    try {
      await procesoAPI.saveProrrateoCelda({ service, instance, clientId, porcentaje: pct });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el prorrateo');
    }
  }, [showNotification]);

  // Reparte un servicio-instancia equitativamente entre los clientes dados
  // (usado al arrastrar un servicio a un cliente). Cada cliente recibe 100/N %.
  const distribuirEquitativo = useCallback(async (service, instance, clientIds) => {
    const k = claveSI(service, instance);
    const n = clientIds.length;
    const dist = {};
    if (n > 0) {
      const base = Math.floor((100 / n) * 100) / 100;
      clientIds.forEach((id) => {
        dist[id] = base;
      });
      const sobrante = Math.round((100 - base * n) * 100) / 100;
      dist[clientIds[0]] = Math.round((base + sobrante) * 100) / 100;
    }
    setProrrateo((prev) => ({ ...prev, [k]: dist }));
    try {
      await procesoAPI.saveProrrateoFila({
        service,
        instance,
        distribucion: clientIds.map((id) => ({ clientId: id, porcentaje: dist[id] }))
      });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar la distribución');
    }
  }, [showNotification]);

  return { prorrateo, loadProrrateo, setCelda, distribuirEquitativo };
};
